import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Session, User } from "@supabase/supabase-js";
import { CustomerDashboard } from "@/components/CustomerDashboard";
import { TradesmanDashboard } from "@/components/TradesmanDashboard";
import { TradesmanProfileSetup } from "@/components/TradesmanProfileSetup";
import { CreateJobDialog } from "@/components/CreateJobDialog";
import { LiveChatWidget } from "@/components/LiveChatWidget";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Clock, Shield, Users, CheckCircle, Wrench, Home, Phone, Mail, FileEdit } from "lucide-react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import heroImage from "@/assets/uk-home-carpenter.jpg";
import heroImageSmall from "@/assets/uk-home-carpenter.jpg";
import heroImage900 from "@/assets/uk-home-carpenter.jpg";
import { CityLandingContent } from "@/components/CityLandingContent";
import { LocalSEO } from "@/components/LocalSEO";
import { HowItWorksSchema } from "@/components/HowItWorksSchema";
import { ServiceSchema } from "@/components/ServiceSchema";
import { PWAInstallPrompt } from "@/components/PWAInstallPrompt";
import { AppDownloadButton } from "@/components/AppDownloadButton";
import { TradesmanCarousel } from "@/components/TradesmanCarousel";
import { useIsMobile } from "@/hooks/use-mobile";
import { ModernLogo } from "@/components/ModernLogo";
import { PasswordResetDialog } from "@/components/PasswordResetDialog";
import { useToast } from "@/hooks/use-toast";
import { useSEO } from "@/hooks/useSEO";
interface Profile {
  user_type: "customer" | "tradesman";
  job_categories?: string[];
  company_name?: string;
  phone?: string;
  insurance_approved?: boolean;
}
const Index = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const {
    toast
  } = useToast();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Get URL parameters for auto-opening chat dialogs from email links
  const focusedJobId = searchParams.get('job');
  const chatAction = searchParams.get('action');
  const chatWithUserId = searchParams.get('with');

  // Get password reset parameters from both query params and URL hash
  const getTokensFromUrl = () => {
    let at = searchParams.get('access_token');
    let rt = searchParams.get('refresh_token');
    let t = searchParams.get('type');

    // Supabase sends tokens in the URL hash (#) for recovery links
    if (!at || !rt || !t) {
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      at = at ?? hashParams.get('access_token');
      rt = rt ?? hashParams.get('refresh_token');
      t = t ?? hashParams.get('type');
    }
    return {
      accessToken: at,
      refreshToken: rt,
      type: t
    };
  };
  const {
    accessToken,
    refreshToken,
    type
  } = getTokensFromUrl();
  console.log('Index: URL parameters detected:', {
    focusedJobId,
    chatAction,
    chatWithUserId,
    accessToken: !!accessToken,
    refreshToken: !!refreshToken,
    type,
    session: !!session,
    user: !!user,
    profile: !!profile,
    currentUrl: window.location.href
  });

  // Ensure a profile row exists for the authenticated user
  const createDefaultProfile = async (u: User): Promise<Profile> => {
    const defaultProfile: Profile & {
      user_id: string;
      email: string;
      phone: string;
    } = {
      user_type: "customer",
      trade_specialties: [],
      company_name: undefined,
      phone: "PHONE_REQUIRED",
      user_id: u.id,
      email: u.email || ""
    } as any;
    const {
      data,
      error
    } = await supabase.from('profiles').insert({
      user_id: u.id,
      email: u.email,
      full_name: u.user_metadata?.full_name || u.email,
      user_type: u.user_metadata?.user_type as 'customer' | 'tradesman' || 'customer',
      phone: u.user_metadata?.phone || 'PHONE_REQUIRED',
      address: u.user_metadata?.address || ''
    }).select('user_type, job_categories, company_name, phone, insurance_approved').maybeSingle();
    if (error) {
      console.error('Failed to create default profile:', error);
      // Return a minimal profile to unblock the UI
      return {
        user_type: 'customer',
        job_categories: [],
        company_name: undefined,
        phone: undefined
      } as Profile;
    }
    return data as Profile ?? {
      user_type: 'customer',
      trade_specialties: [],
      company_name: undefined,
      phone: undefined
    } as Profile;
  };
  useEffect(() => {
    let mounted = true;

    // Timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (mounted && isLoading) {
        console.warn('Loading timeout reached, stopping loading state');
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    // 1) Set up auth state listener FIRST (no async work directly here)
    const {
      data: {
        subscription
      }
    } = supabase.auth.onAuthStateChange((event, nextSession) => {
      if (!mounted) return;
      console.log('Auth event:', event);

      // If user clicked a password recovery link, open the reset dialog even if tokens aren't in the URL
      if (event === 'PASSWORD_RECOVERY') {
        setShowPasswordReset(true);
      }
      setSession(nextSession);
      setUser(nextSession?.user ?? null);
      if (nextSession?.user) {
        // Defer Supabase calls to avoid deadlocks in the callback
        setTimeout(() => {
          if (!mounted) return;
          supabase.from('profiles').select('user_type, job_categories, company_name, phone, insurance_approved').eq('user_id', nextSession.user!.id).maybeSingle().then(async ({
            data,
            error
          }) => {
            if (!mounted) return;
            if (error) {
              console.error('Profile fetch error (auth change):', error);
              // Even if profile fetch fails, stop loading so user can proceed
              setProfile(null);
              setIsLoading(false);
            } else if (!data) {
              console.warn('No profile found on auth change, creating default profile...');
              const created = await createDefaultProfile(nextSession.user!);
              setProfile(created);
            } else {
              console.log('Profile loaded successfully:', data);
              setProfile(data as Profile ?? null);
            }
          }).then(() => {
            // Check admin role
            return supabase.from('user_roles').select('role').eq('user_id', nextSession.user!.id).eq('role', 'admin').maybeSingle();
          }).then(({
            data
          }) => {
            if (!mounted) return;
            setIsAdmin(!!data);
          }).then(() => {
            if (mounted) setIsLoading(false);
          });
        }, 0);
      } else {
        setProfile(null);
        setIsLoading(false);
      }
    });

    // 2) THEN check for existing session
    supabase.auth.getSession().then(({
      data: {
        session: existing
      }
    }) => {
      if (!mounted) return;
      setSession(existing);
      setUser(existing?.user ?? null);
      if (existing?.user) {
        supabase.from('profiles').select('user_type, job_categories, company_name, phone, insurance_approved').eq('user_id', existing.user.id).maybeSingle().then(async ({
          data,
          error
        }) => {
          if (!mounted) return;
          if (error) {
            console.error('Profile fetch error (init):', error);
            setProfile(null);
            setIsLoading(false);
          } else if (!data) {
            console.warn('No profile found on init, creating default profile...');
            const created = await createDefaultProfile(existing.user!);
            setProfile(created);
            setIsLoading(false);
          } else {
            console.log('Profile loaded successfully:', data);
            setProfile(data as Profile ?? null);
            setIsLoading(false);
          }
        });
      } else {
        setIsLoading(false);
      }
    });
    return () => {
      mounted = false;
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []);

  // Handle password reset tokens from URL
  useEffect(() => {
    if (type === 'recovery' && accessToken && refreshToken) {
      // Set the session with the recovery tokens
      supabase.auth.setSession({
        access_token: accessToken,
        refresh_token: refreshToken
      }).then(() => {
        // Show password reset dialog
        setShowPasswordReset(true);

        // Clean up URL parameters (both query params and hash)
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('access_token');
        newUrl.searchParams.delete('refresh_token');
        newUrl.searchParams.delete('type');
        const cleanUrl = newUrl.toString().split('#')[0];
        window.history.replaceState({}, '', cleanUrl);
        window.location.hash = '';
      }).catch(error => {
        console.error('Error setting recovery session:', error);
        toast({
          title: "Invalid reset link",
          description: "This password reset link is invalid or has expired.",
          variant: "destructive"
        });
      });
    }
  }, [type, accessToken, refreshToken, toast]);
  const handleCustomerGetStarted = () => {
    if (session) {
      // If already logged in, go directly to dashboard
      return;
    }

    // If not logged in, navigate to job creation page
    window.location.href = '/create-job';
  };
  const handleTradesmanGetStarted = () => {
    navigate('/auth?type=tradesman');
  };
  const handleJobCreated = () => {
    setShowJobDialog(false);
    toast({
      title: "Job details saved!",
      description: "Now create your account to connect with tradesmen."
    });
    navigate('/auth?type=customer');
  };
  const handleAuthSuccess = () => {
    // This function is no longer needed as auth happens on a separate page
  };
  const handleTradesmanSetupComplete = async () => {
    if (!user) return;
    try {
      const {
        data,
        error
      } = await supabase.from('profiles').select('user_type, job_categories, company_name, phone, insurance_approved').eq('user_id', user.id).maybeSingle();
      if (!error && data) {
        setProfile(data as Profile ?? null);
        // Show success message after dashboard is shown
        setTimeout(() => {
          toast({
            title: "Profile setup complete!",
            description: "You're all set to start receiving job notifications."
          });
        }, 300);
      }
    } catch (err) {
      console.error('Error refreshing profile after setup:', err);
    }
  };
  const handleSignOut = () => {
    setSession(null);
    setUser(null);
    setProfile(null);
  };
  if (isLoading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading Sortable...</p>
        </div>
      </div>;
  }

  // Show appropriate dashboard if user is authenticated
  if (session && user && profile) {
    console.log('Index: Rendering dashboard for authenticated user', {
      userType: profile.user_type
    });
    if (profile.user_type === "customer") {
      return <CustomerDashboard onSignOut={handleSignOut} focusedJobId={focusedJobId} chatAction={chatAction} chatWithUserId={chatWithUserId} showPasswordReset={showPasswordReset} onPasswordResetChange={setShowPasswordReset} />;
    } else if (profile.user_type === "tradesman") {
      // Check if tradesman needs to complete profile setup
      const needsProfileSetup = !profile.job_categories || profile.job_categories.length === 0 || !profile.company_name || !profile.phone;
      if (needsProfileSetup) {
        return <TradesmanProfileSetup onComplete={handleTradesmanSetupComplete} />;
      }
      return <TradesmanDashboard onSignOut={handleSignOut} chatAction={chatAction} chatWithUserId={chatWithUserId} focusedJobId={focusedJobId} showPasswordReset={showPasswordReset} onPasswordResetChange={setShowPasswordReset} />;
    }
  }

  // Handle authenticated user but no profile loaded yet
  if (session && user && !profile) {
    console.log('Index: User authenticated but profile loading...');
    return <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
        <div className="text-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading your profile...</p>
          <Button 
            variant="outline" 
            onClick={async () => {
              await supabase.auth.signOut();
              handleSignOut();
            }}
          >
            Sign Out
          </Button>
        </div>
      </div>;
  }

  // Handle unauthenticated users
  if (!session || !user) {
    console.log('Index: No session, showing landing page');
    return <LandingPage isAdmin={isAdmin} chatAction={chatAction} focusedJobId={focusedJobId} chatWithUserId={chatWithUserId} />;
  }

  // Fallback - show loading if we somehow reach here
  console.log('Index: Fallback loading state', {
    session: !!session,
    user: !!user,
    profile: !!profile
  });
  return <div className="min-h-screen flex items-center justify-center bg-gradient-subtle">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
        <p className="text-muted-foreground">Loading...</p>
      </div>
    </div>;
};
const LandingPage = ({
  isAdmin = false,
  chatAction,
  focusedJobId,
  chatWithUserId
}: {
  isAdmin?: boolean;
  chatAction?: string | null;
  focusedJobId?: string | null;
  chatWithUserId?: string | null;
}) => {
  const [showJobDialog, setShowJobDialog] = useState(false);
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const {
    toast
  } = useToast();
  const isMobile = useIsMobile();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // Enhanced SEO optimization for landing page
  useSEO({
    title: "Compare Local Tradesmen | Get 5 Free Quotes | Sortable",
    description: "Compare quotes from verified UK tradesmen — plumbers, electricians, builders & more. Get up to 5 free quotes from fully insured local pros.",
    keywords: "compare tradesmen, get quotes, free tradesman quotes, compare local tradespeople, hire tradesperson, trusted tradesmen, verified contractors, local builders, plumber near me, electrician near me, heating engineer, carpenter, handyman, local tradesman, post job online, compare quotes, fully insured tradesmen, property maintenance, home repairs, find builder, find plumber, find electrician, tradesman directory, local trades, home improvement, building work, reliable contractors, skilled tradesmen, tradesman comparison",
    canonical: "https://sortable.uk/",
    ogImage: "https://sortable.uk/hero-tradesman.jpg",
    ogType: "website"
  });

  // Get password reset parameters from URL
  const accessToken = searchParams.get('access_token');
  const refreshToken = searchParams.get('refresh_token');
  const type = searchParams.get('type');

  // When coming from a recovery email link, set session and open reset dialog
  useEffect(() => {
    let at = accessToken || undefined;
    let rt = refreshToken || undefined;
    let t = type || undefined;

    // Supabase sends tokens in the URL hash (#) for recovery links
    if (!at || !rt || !t) {
      const hashParams = new URLSearchParams(window.location.hash.slice(1));
      at = at ?? hashParams.get('access_token') ?? undefined;
      rt = rt ?? hashParams.get('refresh_token') ?? undefined;
      t = t ?? hashParams.get('type') ?? undefined;
    }
    if (t === 'recovery' && at && rt) {
      supabase.auth.setSession({
        access_token: at,
        refresh_token: rt
      }).then(() => {
        setShowPasswordReset(true);
        // Clean URL (remove query and hash tokens)
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('access_token');
        newUrl.searchParams.delete('refresh_token');
        newUrl.searchParams.delete('type');
        window.history.replaceState({}, '', newUrl.toString().split('#')[0]);
        window.location.hash = '';
      }).catch(error => {
        console.error('Error setting recovery session (LandingPage):', error);
        toast({
          title: 'Invalid reset link',
          description: 'This password reset link is invalid or has expired.',
          variant: 'destructive'
        });
      });
    }
  }, [type, accessToken, refreshToken, toast]);
  // If coming from email link with chat intent, show appropriate message
  const hasEmailChatIntent = chatAction === 'chat' && focusedJobId && chatWithUserId;
  const handleCustomerGetStarted = () => {
    // Navigate to job creation page for new customers
    window.location.href = '/create-job';
  };
  const handleTradesmanGetStarted = () => {
    navigate('/auth?type=tradesman');
  };
  const handleJobCreated = () => {
    setShowJobDialog(false);
    toast({
      title: "Job details saved!",
      description: "Now create your account to connect with tradesmen."
    });
    navigate('/auth?type=customer');
  };
  const handleAuthSuccess = () => {
    // This function is no longer used as auth happens on a separate page
  };
  // Show email chat intent message - redirect to auth page instead
  if (hasEmailChatIntent) {
    return <div className="min-h-screen bg-gradient-subtle flex items-center justify-center p-4">
        <Card className="max-w-md mx-auto shadow-elegant">
          <CardHeader className="text-center">
            <CardTitle className="text-2xl">Sign in to Continue</CardTitle>
             <CardDescription className="text-foreground">
               You clicked a chat link from an email. Please sign in to your account to open the conversation.
             </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <Button onClick={() => navigate('/auth')} variant="hero" size="lg" className="w-full">
              Sign In to Open Chat
            </Button>
             <p className="text-sm text-foreground text-center">
               Don't have an account? You'll be able to create one after clicking above.
             </p>
          </CardContent>
        </Card>
      </div>;
  }
  return <div className="min-h-screen bg-gradient-subtle">
        <LocalSEO />
        <HowItWorksSchema />
        <ServiceSchema />
        <PWAInstallPrompt />
        {/* Modern Header */}
        <header className="relative bg-gradient-to-r from-background via-background/95 to-background backdrop-blur-lg border-b border-border/50 shadow-elegant sticky top-0 z-50">
          <div className="absolute inset-0 bg-gradient-hero opacity-5"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className={`flex justify-between items-center ${isMobile ? 'py-2' : 'py-4'}`}>
              {/* Brand Section */}
              <div className="flex items-center relative z-20">
                <ModernLogo size="sm" showTagline={false} />
              </div>

              {/* Navigation & Action Buttons */}
              <div className={`flex items-center ${isMobile ? 'gap-1' : 'gap-3'}`}>
                <Link to="/why-us" className={isMobile ? 'hidden' : ''}>
                  <Button variant="outline" size={isMobile ? "sm" : "default"} className="shadow-sm hover:shadow-md transition-all duration-200">
                    <Shield className={`${isMobile ? 'h-3 w-3 mr-1' : 'h-4 w-4 mr-2'}`} />
                    {isMobile ? 'Why Us' : 'Why Choose Us'}
                  </Button>
                </Link>
                <Link to="/for-tradesmen" className={isMobile ? 'hidden' : ''}>
                  <Button variant="outline" size={isMobile ? "sm" : "default"} className="shadow-sm hover:shadow-md transition-all duration-200">
                    Tradesman Registration
                  </Button>
                </Link>
                {isAdmin && <Link to="/admin">
                    <Button variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200">
                      Admin
                    </Button>
                  </Link>}
                <Button onClick={() => navigate('/auth')} variant="hero" size={isMobile ? "sm" : "lg"} className={`shadow-glow hover:shadow-elegant transition-all duration-300 font-semibold ${isMobile ? 'px-3 py-1.5 text-sm' : ''}`}>
                  {isMobile ? 'Start' : 'Get Started'}
                </Button>
                {isMobile && <>
                    <Link to="/why-us">
                      <Button variant="outline" size="sm" aria-label="Why Us" className="shadow-sm hover:shadow-md transition-all duration-200 px-2">
                        <Shield className="h-3 w-3" aria-hidden="true" />
                      </Button>
                    </Link>

                    <Link to="/for-tradesmen">
                      <Button variant="outline" size="sm" className="shadow-sm hover:shadow-md transition-all duration-200 text-xs">
                        Trade Reg.
                      </Button>
                    </Link>
                  </>}
              </div>
            </div>
          </div>
        </header>

      {/* Hero Section - Enhanced for Conversion */}
      <section className="relative py-16 lg:py-24 bg-gradient-hero overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary via-primary-hover to-primary opacity-95"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="text-white">
              <div className="inline-flex items-center gap-2 bg-accent-orange backdrop-blur-sm px-4 py-2 rounded-full text-white text-sm font-semibold mb-6 animate-pulse shadow-orange">
                <span className="relative flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-white"></span>
                </span>
                10,000+ Jobs Posted This Year
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6">
                Find Your Perfect Tradesman
                <span className="text-accent-orange block mt-2">In 60 Seconds</span>
              </h1>
              
              <p className="text-2xl mb-8 leading-relaxed text-white/95">
                Post your job once. Get up to <span className="font-bold text-accent-orange">5 FREE quotes</span> from verified local professionals. Compare and hire the best!
              </p>
              
              <div className="flex flex-col gap-4 mb-8">
                <Button onClick={handleCustomerGetStarted} className="bg-accent-orange hover:bg-accent-orange-hover text-white shadow-orange text-xl font-bold py-8 group relative overflow-hidden">
                  <span className="relative z-10 flex items-center gap-3">
                    <Home className="h-6 w-6" />
                    Post My Job - FREE
                    <span className="ml-2">→</span>
                  </span>
                  <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform"></div>
                </Button>
                
                <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="bg-green-500 rounded-full p-1">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span>No Payment Required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-green-500 rounded-full p-1">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span>Verified Tradesmen Only</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="bg-green-500 rounded-full p-1">
                      <CheckCircle className="h-4 w-4 text-white" />
                    </div>
                    <span>Takes 60 Seconds</span>
                  </div>
                </div>
              </div>
              
              <div className="flex items-center gap-4 pt-6 border-t border-white/20">
                <div className="flex -space-x-3">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <div key={i} className="w-12 h-12 rounded-full bg-accent-orange border-3 border-primary flex items-center justify-center text-white font-bold shadow-lg">
                      {String.fromCharCode(64 + i)}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <span key={i} className="text-yellow-400 text-lg">★</span>
                    ))}
                  </div>
                  <p className="text-sm"><span className="font-bold text-lg">4.9/5</span> from 2,500+ reviews</p>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <div className="w-full aspect-[4/3] bg-white rounded-3xl overflow-hidden shadow-glow">
                <img src={heroImageSmall} srcSet={`${heroImageSmall} 512w, ${heroImage900} 900w, ${heroImage900} 1200w`} sizes="(max-width: 768px) 100vw, (max-width: 1200px) 50vw, 380px" alt="Professional tradesman working on home maintenance" className="w-full h-full object-cover" width="584" height="438" loading="eager" fetchPriority="high" decoding="async" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-white p-6 rounded-2xl shadow-glow">
                <div className="flex items-center gap-3">
                  <div className="bg-green-100 p-3 rounded-full">
                    <Clock className="h-8 w-8 text-green-600" />
                  </div>
                  <div>
                    <p className="font-bold text-black text-lg">2 Hour Response</p>
                    <p className="text-sm text-muted-foreground">Average quote time</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Urgency Bar */}
      <section className="bg-accent-orange text-white py-4">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <Clock className="h-6 w-6 animate-pulse" />
              <p className="font-bold text-lg">Limited Time: Get matched with tradesmen in under 2 hours!</p>
            </div>
            <Button onClick={handleCustomerGetStarted} className="bg-white text-accent-orange hover:bg-gray-100 font-bold whitespace-nowrap">
              Post Job Now →
            </Button>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-muted/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-5xl font-bold text-primary mb-2">10k+</div>
              <p className="text-muted-foreground font-medium">Jobs Posted</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-primary mb-2">5k+</div>
              <p className="text-muted-foreground font-medium">Verified Tradesmen</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-primary mb-2">98%</div>
              <p className="text-muted-foreground font-medium">Happy Customers</p>
            </div>
            <div>
              <div className="text-5xl font-bold text-primary mb-2">2hrs</div>
              <p className="text-muted-foreground font-medium">Avg Response</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-sm font-bold text-primary uppercase tracking-wide mb-3">Simple Process</p>
            <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
              How It Works
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From posting to hiring - everything happens in minutes
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <Card className="text-center hover:shadow-glow transition-all hover:scale-105 border-2 hover:border-primary relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-accent-orange text-white px-4 py-1 rounded-full text-sm font-bold">
                  Step 1
                </div>
              </div>
              <CardHeader className="pt-8">
                <div className="bg-gradient-orange w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-orange">
                  <Wrench className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl">Post Your Job FREE</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-foreground leading-relaxed">
                  Describe what you need in 60 seconds. Add photos and location. Completely free, no payment required!
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-glow transition-all hover:scale-105 border-2 hover:border-primary relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-accent-orange text-white px-4 py-1 rounded-full text-sm font-bold">
                  Step 2
                </div>
              </div>
              <CardHeader className="pt-8">
                <div className="bg-gradient-orange w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-orange">
                  <Users className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl">Get 5 FREE Quotes</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-foreground leading-relaxed">
                  Verified local tradesmen send you quotes. Compare prices, ratings, reviews, and portfolios side-by-side.
                </CardDescription>
              </CardContent>
            </Card>
            
            <Card className="text-center hover:shadow-glow transition-all hover:scale-105 border-2 hover:border-primary relative">
              <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                <div className="bg-accent-orange text-white px-4 py-1 rounded-full text-sm font-bold">
                  Step 3
                </div>
              </div>
              <CardHeader className="pt-8">
                <div className="bg-gradient-orange w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-orange">
                  <CheckCircle className="h-10 w-10 text-white" />
                </div>
                <CardTitle className="text-2xl">Hire with Confidence</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base text-foreground leading-relaxed">
                  Choose the best tradesman for your needs. Chat directly and get your job done right, guaranteed!
                </CardDescription>
              </CardContent>
            </Card>
          </div>

          <div className="text-center mt-12">
            <Button onClick={handleCustomerGetStarted} className="bg-accent-orange hover:bg-accent-orange-hover text-white shadow-orange text-xl font-bold py-8 px-12">
              Start Your Free Job Post Now →
            </Button>
            <p className="text-sm text-muted-foreground mt-4">Takes 60 seconds • No credit card required • 100% Free</p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gradient-subtle">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-bold text-black mb-4">
              Why 10,000+ Customers Trust Us
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            <Card className="hover:shadow-glow transition-all hover:scale-105">
              <CardHeader>
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">100% Verified Pros</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Every tradesman is background checked, insurance verified, and reviewed. No cowboys!</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-glow transition-all hover:scale-105">
              <CardHeader>
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Lightning Fast</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Get quotes in under 2 hours. Most jobs receive responses within 24 hours.</p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-glow transition-all hover:scale-105">
              <CardHeader>
                <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center mb-3">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <CardTitle className="text-xl">Best Price Guarantee</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">Compare up to 5 quotes to ensure you never overpay. Save an average of 23%!</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
             <h2 className="text-3xl md:text-4xl font-bold text-black mb-4">
               About Sortable
            </h2>
             <p className="text-xl text-black max-w-4xl mx-auto">
               Founded on the principles of reliability, transparency, and excellence, Sortable has revolutionized the property maintenance industry by creating the UK's most trusted network of verified tradesmen. We bridge the gap between property owners seeking quality workmanship and skilled professionals looking for meaningful projects, ensuring every connection results in outstanding outcomes that exceed expectations.
             </p>
          </div>
          
          <div className="grid lg:grid-cols-2 gap-8 items-center">
            <div>
               <h3 className="text-2xl font-bold text-black mb-6">
                 Compare Quotes from Trusted Tradespeople
              </h3>
               <p className="text-black mb-6 leading-relaxed">
                 At Sortable, we make it simple to find and compare local tradespeople. Our platform connects you with up to 5 verified professionals who provide competitive quotes for your job. Every tradesperson in our network has been thoroughly vetted through our comprehensive screening process, ensuring they possess the necessary qualifications, insurance coverage, and proven track record. Compare prices, read genuine reviews, and make informed decisions with confidence.
               </p>
              
              <h4 className="text-xl font-semibold text-black mb-4">Our Services Include:</h4>
              <div className="grid sm:grid-cols-2 gap-3 mb-6">
                 <div className="flex items-center gap-2">
                   <CheckCircle className="h-4 w-4 text-green-600" />
                   <span className="text-sm text-black">Home Repairs</span>
                 </div>
                 <div className="flex items-center gap-2">
                   <CheckCircle className="h-4 w-4 text-green-600" />
                   <span className="text-sm text-black">Plumbing Services</span>
                 </div>
                 <div className="flex items-center gap-2">
                   <CheckCircle className="h-4 w-4 text-green-600" />
                   <span className="text-sm text-black">Electrical Work</span>
                 </div>
                 <div className="flex items-center gap-2">
                   <CheckCircle className="h-4 w-4 text-green-600" />
                   <span className="text-sm text-black">General Maintenance</span>
                 </div>
                 <div className="flex items-center gap-2">
                   <CheckCircle className="h-4 w-4 text-green-600" />
                   <span className="text-sm text-black">Heating & Gas</span>
                 </div>
                 <div className="flex items-center gap-2">
                   <CheckCircle className="h-4 w-4 text-green-600" />
                   <span className="text-sm text-black">Carpentry & Joinery</span>
                </div>
              </div>
              
              <div className={`${isMobile ? 'space-y-3 mb-8' : 'flex flex-col gap-4 max-w-sm mx-auto'}`}>
                <Link to="/about">
                  <Button variant="outline" size={isMobile ? "default" : "lg"} className="w-full">
                    Learn More About Us
                  </Button>
                </Link>
                <AppDownloadButton variant="outline" size={isMobile ? "default" : "lg"} className="w-full" />
                <Button onClick={() => navigate('/auth')} variant="hero" size={isMobile ? "default" : "lg"} className="w-full">
                  Get Started Today
                </Button>
              </div>
            </div>
            
            <div className={`${isMobile ? 'space-y-6 mt-8' : 'space-y-6'}`}>
              <Card className="shadow-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Shield className="h-6 w-6 text-primary" />
                    Safety & Insurance
                  </CardTitle>
                </CardHeader>
                <CardContent>
                   <p className="text-black">Every professional in our network undergoes rigorous safety assessments and maintains comprehensive insurance coverage including public liability, employer's liability, and professional indemnity insurance. All tradesmen hold current industry certifications and undergo regular compliance reviews to ensure they meet our strict safety and quality standards.</p>
                </CardContent>
              </Card>
              
              <Card className="shadow-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Users className="h-6 w-6 text-accent-orange" />
                    Verified Professionals
                  </CardTitle>
                </CardHeader>
                <CardContent>
                   <p className="text-black">
                     Our comprehensive verification process includes qualification authentication, reference checks, work portfolio assessment, and continuous performance monitoring. Each tradesman must demonstrate proven expertise in their field, maintain excellent customer feedback ratings, and adhere to our strict code of professional conduct. This multi-layered approach ensures you receive consistently high-quality service from trustworthy professionals who take pride in their craftsmanship.
                   </p>
                </CardContent>
              </Card>
              
              <Card className="shadow-elegant">
                <CardHeader>
                  <CardTitle className="flex items-center gap-3">
                    <Clock className="h-6 w-6 text-green-600" />
                    24/7 Support
                  </CardTitle>
                </CardHeader>
                <CardContent>
                   <p className="text-black">
                     Our dedicated customer support team provides comprehensive assistance throughout your entire project journey. From initial job posting guidance to dispute resolution, our experienced support specialists are available 24/7 via phone, email, and live chat. We offer technical assistance, project advice, and proactive monitoring to ensure your experience exceeds expectations at every touchpoint.
                   </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-12 bg-gradient-hero">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Ready to Compare Quotes and Find Your Perfect Tradesperson?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Join thousands of satisfied customers who trust Sortable to compare local tradespeople. Get up to 5 free quotes, read genuine reviews, and hire verified professionals with confidence. Experience hassle-free comparisons and quality workmanship every time.
          </p>
          <Button onClick={() => navigate('/auth')} variant="orange" size="xl">
            Join Sortable Today
          </Button>
        </div>
      </section>

      {/* Footer */}
       <footer className="bg-gray-900 text-white py-12">
         <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
           <div className="grid md:grid-cols-4 gap-8">
             <div>
               <h3 className="text-xl font-bold mb-4 text-white">Sortable™</h3>
                <p className="text-gray-200 mb-4">
                  The UK's leading platform connecting property owners with verified, insured tradesmen. Experience premium quality workmanship with our fast tradesman response guarantee for all your property maintenance needs.
                </p>
               <p className="text-sm text-gray-300">
                 Trademark of Realise Property Maintenance Ltd.
               </p>
            </div>
            
             <div>
               <h4 className="font-semibold mb-4 text-white">For Customers</h4>
               <div className="space-y-2 text-gray-200">
                 <div><Link to="/how-it-works-customer" className="hover:text-white">How It Works</Link></div>
                 <div><Link to="/about" className="hover:text-white">About Us</Link></div>
                 <div><Link to="/success-stories" className="hover:text-white">Success Stories</Link></div>
               </div>
            </div>

             <div>
               <h4 className="font-semibold mb-4 text-white">For Tradesmen</h4>
               <div className="space-y-2 text-gray-200">
                 <div><Link to="/how-it-works" className="hover:text-white">How It Works</Link></div>
                 <div><Link to="/for-tradesmen" className="hover:text-white">Join Platform</Link></div>
                 <div><Link to="/pricing" className="hover:text-white">Pricing</Link></div>
               </div>
            </div>
            
             <div>
                <h4 className="font-semibold mb-4 text-white">Services</h4>
                <div className="text-gray-200">
                  <Link to="/services/plumbing" className="hover:text-white">Plumbing</Link>, 
                  <Link to="/services/electrical" className="hover:text-white"> Electrical</Link>, 
                  <Link to="/services/carpentry" className="hover:text-white"> Carpentry</Link>, 
                  <Link to="/services/heating" className="hover:text-white"> Heating</Link>, 
                  <Link to="/services/roofing" className="hover:text-white"> Roofing</Link>, 
                  <Link to="/services/painting" className="hover:text-white"> Painting</Link>, 
                  <Link to="/services/tiling" className="hover:text-white"> Tiling</Link>, 
                  <Link to="/services/landscaping" className="hover:text-white"> Landscaping</Link>
                </div>
             </div>
             
             <div>
                <h4 className="font-semibold mb-4 text-white">Contact</h4>
                <div className="space-y-2 text-gray-200">
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4" />
                    <span>0800 043 8880</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4" />
                    <span>realise@post.com</span>
                  </div>
                  </div>
                </div>
          </div>
          
           <div className="border-t border-gray-600 mt-8 pt-8 text-center text-gray-300">
             <p>&copy; 2025 Sortable. All rights reserved.</p>
          </div>
        </div>
      </footer>
      
      {/* Live Chat Widget */}
      <LiveChatWidget />
      
      {/* Job Creation Dialog */}
    {showJobDialog && <CreateJobDialog open={showJobDialog} onOpenChange={setShowJobDialog} onJobCreated={handleJobCreated} />}
      
      {/* Password Reset Dialog */}
      <PasswordResetDialog open={showPasswordReset} onOpenChange={setShowPasswordReset} />
    </div>;
};
export default Index;